import moment from 'moment';
import React from 'react';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';
import APPLICATION_DOCUMENT_STATUSES_ENUM from '@api/requests/applications/enums/APPLICATION_DOCUMENT_STATUSES_ENUM.enum';
import TAX_FORM_STATUSES_ENUM from '@api/requests/applications/enums/TAX_FORM_STATUSES_ENUM.enum';
import ApplicationDetailsInterface from '@api/requests/applications/interfaces/ApplicationDetailsInterface.interface';
import { FileStorageServiceSettings } from '@api/requests/auth/interfaces/CurrentUserBranch.interface';

import { showToast } from '@app/providers/ToastProvider';
import { getApplicationFormIdentifierQuestionsAnswers } from '@app/redux/applicationForm/applicationForm.selectors';
import { getCurrentApplicationActions, getTaxFormListPDF } from '@app/redux/applications/applications.selectors';
import { useAppSelector } from '@app/redux/hooks';

import { Download, Verified } from '@mui/icons-material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Box, Button, CircularProgress, Divider, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import FileManager from '@components/fileManager';

import {
	getApplicationAccountDocumentIcon,
	getApplicationAccountTypeIcon,
} from '@pages/Applications/containers/ApplicationAccountCardList/components/utils';
import { useDownloadApplicationPDF } from '@pages/Applications/containers/ApplicationAccountDetails/components/InfoSection/containers/DownloadApplicationPdfButton/hooks';
import { StyledDivider } from '@pages/Applications/styles';

import * as environment from '@utils/environment';

import SuccessIcon from 'jsx:@assets/svgs/cloud.svg';
import FolderIcon from 'jsx:@assets/svgs/folder.svg';

import CustodianAgreements from './components/CustodianAgreements';
import { w8BenReminderTexts } from './constants';
import { StyledInfoSection } from './styles';

const EMPTY_VALUE_LABEL = 'Does not exist';

function DetailsSection({ applicationDetails }: { applicationDetails: ApplicationDetailsInterface }) {
	const applicationFormIdentifierQuestionsAnswers = useAppSelector(getApplicationFormIdentifierQuestionsAnswers);
	const currentApplicationActions = useAppSelector(getCurrentApplicationActions);
	const taxFormListPDF = useAppSelector(getTaxFormListPDF);
	const { handleDownloadApplicationPDF, isApplicationPDFDownloading } = useDownloadApplicationPDF(
		applicationDetails,
		'tax-form',
	);

	const { isPending: isPendingSaveAnswer, startRequest: saveFileAnswer } = useTriggeredRequest(
		AccountClientRequests.Applications.saveFileAnswer,
	);

	const { isPending: isPendingGenerateSignLink, startRequest: generateSignLink } = useTriggeredRequest(
		AccountClientRequests.Applications.generateSignLink,
	);

	const { isPending: isPendingRecertifyW8BenForm, startRequest: recertifyW8BenForm } = useTriggeredRequest(
		AccountClientRequests.Applications.recertifyW8BenForm,
	);

	const handleSaveAnswer = async (file: File, questionId: number) => {
		try {
			await saveFileAnswer({
				application_id: applicationDetails.id,
				question_id: questionId,
				file,
			});
		} catch (e) {
			showToast.error('Something went wrong!');
		}
	};

	const handleGenerateSignLink = async () => {
		try {
			if (applicationDetails?.current_custodian_request?.id) {
				const { request_to_sign: requestToSign } = await generateSignLink({
					custodian_request_id: applicationDetails.current_custodian_request.id,
					application_id: applicationDetails.id,
					type: 'tax-form',
				});

				if (requestToSign?.sign_link) {
					window.open(requestToSign.sign_link, '_blank', 'noopener,noreferrer');
				}
			}
		} catch (e) {
			showToast.error('Something went wrong!');
		}
	};

	const handleRecertifyW8Ben = async () => {
		try {
			if (applicationDetails?.id) {
				await recertifyW8BenForm({
					application_id: applicationDetails.id,
				});
			}
		} catch (e) {
			showToast.error('Something went wrong!');
		}
	};

	const accountTypeIcon = getApplicationAccountTypeIcon(applicationFormIdentifierQuestionsAnswers.account_type);
	const isSignedTaxForm =
		currentApplicationActions.generateSignLink.errorMessage === 'Tax form can not be generated. Status is: signed';
	const isUnsignedTaxForm = taxFormListPDF?.[0]?.status !== TAX_FORM_STATUSES_ENUM.SIGNED;
	const signLinkTaxForm = taxFormListPDF?.[0]?.sign_link;

	const fileStorageService = applicationDetails.branch.services.find(service => service.name === 'file_storage');
	const isFileStorageServiceEnabled = fileStorageService?.is_enabled === 1;
	const fileStorageSettings = fileStorageService?.settings as FileStorageServiceSettings | undefined;
	const isTaxFormRequestToSignEnabled =
		isFileStorageServiceEnabled &&
		((fileStorageSettings?.w9_file && fileStorageSettings?.['w9_file.behavior'] === 'request_to_sign') ||
			(fileStorageSettings?.w8_ben_file && fileStorageSettings?.['w8_ben_file.behavior'] === 'request_to_sign'));

	const hasCustomerTypeAnswer = applicationFormIdentifierQuestionsAnswers.customer_type !== EMPTY_VALUE_LABEL;
	const hasAccountTypeAnswer = applicationFormIdentifierQuestionsAnswers.account_type !== EMPTY_VALUE_LABEL;
	const hasOptionsAnswer = applicationFormIdentifierQuestionsAnswers.options !== EMPTY_VALUE_LABEL;
	const isAllowRecertifyW8Ben =
		currentApplicationActions.recertifyW8Ben?.isAllow &&
		applicationDetails?.w8_warning_at &&
		new Date() > new Date(applicationDetails.w8_warning_at);
	const isW8Warning =
		applicationDetails?.w8_warning_at !== null && new Date() >= new Date(applicationDetails.w8_warning_at);
	const isW8Expired =
		applicationDetails?.w8_expires_at !== null && new Date() >= new Date(applicationDetails.w8_expires_at);
	const showDownloadButton = currentApplicationActions.downloadTaxForm.isAllow === true;

	return (
		<StyledInfoSection>
			{isW8Expired || isW8Warning ? (
				<div>
					<Grid
						item
						xs={12}
						px={2}
						style={{ backgroundColor: isW8Expired ? '#f7b5af' : '#fbe0a8', borderRadius: '4px' }}
						m={1}
						p={0}
					>
						<Grid container>
							<Grid item xs={12}>
								<Stack alignItems={'center'} justifyContent={'space-between'} direction={'row'}>
									<Typography variant={'caption'} m={1}>
										<b>{w8BenReminderTexts[isW8Expired ? 'w8Expires' : 'w8Warning']}</b>
									</Typography>
								</Stack>
							</Grid>
						</Grid>
					</Grid>
					<StyledDivider orientation="horizontal" flexItem />
				</div>
			) : null}

			{hasCustomerTypeAnswer || hasAccountTypeAnswer || hasOptionsAnswer ? (
				<>
					<Grid item xs={12} px={2}>
						<Grid container columnSpacing={5} rowSpacing={3}>
							{hasCustomerTypeAnswer ? (
								<Grid item xs={6} sm={6} md={4}>
									<Stack direction={'column'}>
										<Typography color="textSecondary" variant={'caption'}>
											Customer type
										</Typography>

										<Typography variant={'body2'}>{applicationFormIdentifierQuestionsAnswers.customer_type}</Typography>
									</Stack>
								</Grid>
							) : null}

							{hasAccountTypeAnswer ? (
								<Grid item xs={6} sm={6} md={4}>
									<Stack direction={'column'}>
										<Typography color="textSecondary" variant={'caption'}>
											Account type
										</Typography>
										<Stack direction={'row'} alignItems={'center'}>
											{accountTypeIcon}

											<Typography variant={'body2'} ml={accountTypeIcon ? 0.5 : 0}>
												{environment.env.FIRM_ENVIRONMENT === 'axos-crypto'
													? 'Crypto'
													: applicationFormIdentifierQuestionsAnswers.account_type}
											</Typography>
										</Stack>
									</Stack>
								</Grid>
							) : null}

							{hasOptionsAnswer ? (
								<Grid item xs={6} sm={6} md={4}>
									<Stack direction={'column'}>
										<Typography color="textSecondary" variant={'caption'}>
											Options
										</Typography>

										<Typography variant={'body2'}>{applicationFormIdentifierQuestionsAnswers.options}</Typography>
									</Stack>
								</Grid>
							) : null}
						</Grid>
					</Grid>
					<StyledDivider orientation="horizontal" flexItem />
				</>
			) : null}

			<Grid item xs={12} px={2}>
				<Grid container columnSpacing={5} rowSpacing={3}>
					<Grid item xs={6} sm={6} md={4}>
						<Stack direction={'column'}>
							<Typography color="textSecondary" variant={'caption'}>
								Created
							</Typography>

							<Typography variant={'body2'}>
								{applicationDetails.timestamps.created_at
									? moment(applicationDetails.timestamps.created_at).format('MMM Do, YYYY [at] h:mm A')
									: 'N/A'}
							</Typography>
						</Stack>
					</Grid>

					<Grid item xs={6} sm={6} md={4}>
						<Stack direction={'column'}>
							<Typography color="textSecondary" variant={'caption'}>
								Last updated
							</Typography>

							<Typography variant={'body2'}>
								{applicationDetails.timestamps.updated_at
									? moment(applicationDetails.timestamps.updated_at).format('MMM Do, YYYY [at] h:mm A')
									: 'N/A'}
							</Typography>
						</Stack>
					</Grid>

					<Grid item xs={6} sm={6} md={4}>
						<Stack direction={'column'}>
							<Typography color="textSecondary" variant={'caption'}>
								Submitted
							</Typography>

							<Typography variant={'body2'}>
								{applicationDetails.submitted_at
									? moment(applicationDetails.submitted_at).format('MMM Do, YYYY [at] h:mm A')
									: 'N/A'}
							</Typography>
						</Stack>
					</Grid>
				</Grid>
			</Grid>

			<StyledDivider orientation="horizontal" flexItem />

			<Grid item xs={12} px={2}>
				<Typography variant={'body1'} mb={5}>
					<b>Uploads</b>
				</Typography>

				{applicationDetails?.documents.length ? (
					<Grid container rowSpacing={2}>
						{applicationDetails?.documents.map(document => (
							<Grid item xs={12} key={document.id}>
								<Stack alignItems={'center'} justifyContent={'space-between'} direction={'row'}>
									<Typography variant={'caption'}>
										<b>{document.answer?.question?.translations?.[0].data || 'Document'}</b>
									</Typography>

									<Grid item display={'flex'} alignItems={'center'}>
										{getApplicationAccountDocumentIcon(document.current_status.status)}

										<Typography variant={'caption'} ml={1.5} sx={{ textTransform: 'capitalize' }} mr={2}>
											{document.current_status.status === 'pending' ? 'Received' : document.current_status.status}
										</Typography>

										<Divider orientation="vertical" flexItem />

										<Box ml={1}>
											<Tooltip title="Re-upload file" placement="top">
												<FileManager
													type={'document-image'}
													onChange={newFile => handleSaveAnswer(newFile, document.answer.question.id)}
												>
													{({ handleOpenFileProvider }) => (
														<IconButton
															onClick={handleOpenFileProvider}
															disabled={document.current_status.status !== APPLICATION_DOCUMENT_STATUSES_ENUM.DECLINED}
														>
															{isPendingSaveAnswer ? <CircularProgress size={20} /> : <FileUploadIcon />}
														</IconButton>
													)}
												</FileManager>
											</Tooltip>
										</Box>
									</Grid>
								</Stack>
							</Grid>
						))}
					</Grid>
				) : (
					<Grid container alignItems={'center'} justifyContent={'center'} flexDirection={'column'} pt={2}>
						<FolderIcon />

						<Typography color="textSecondary" variant={'caption'} mt={1}>
							There aren&#39;t any uploaded documents yet
						</Typography>
					</Grid>
				)}
			</Grid>

			<CustodianAgreements applicationDetails={applicationDetails} />

			{isTaxFormRequestToSignEnabled && (isUnsignedTaxForm || currentApplicationActions.generateSignLink.isAllow) ? (
				<>
					<StyledDivider orientation="horizontal" flexItem />

					<Grid item xs={12} px={2}>
						<Grid container rowSpacing={2}>
							<Grid item xs={12}>
								<Stack alignItems={'center'} justifyContent={'space-between'} direction={'row'}>
									<Typography variant={'caption'}>
										<b>Sign Tax Form</b>
									</Typography>
									<Grid item display={'flex'} alignItems={'center'}>
										<Button
											variant={'contained'}
											color={'secondary'}
											size={'small'}
											onClick={() => {
												if (signLinkTaxForm && isUnsignedTaxForm) {
													window.open(signLinkTaxForm, '_blank', 'noopener,noreferrer');
												} else {
													handleGenerateSignLink();
												}
											}}
											disabled={isPendingGenerateSignLink}
											sx={{ textTransform: 'initial', width: 150, height: 32 }}
										>
											{isPendingGenerateSignLink ? (
												<CircularProgress size={15} color={'info'} variant={'indeterminate'} />
											) : (
												<Typography variant={'caption'} ml={1}>
													<b>Sign Tax Form</b>
												</Typography>
											)}
										</Button>
									</Grid>
								</Stack>
							</Grid>
						</Grid>
					</Grid>
				</>
			) : null}

			{isSignedTaxForm ? (
				<>
					<StyledDivider orientation="horizontal" flexItem />

					<Grid item xs={12} px={2}>
						<Grid container rowSpacing={2}>
							<Grid item xs={12}>
								<Stack alignItems={'center'} justifyContent={'space-between'} direction={'row'}>
									<Typography variant={'caption'}>
										<b>Tax form is signed. A copy has been sent to your email.</b>
									</Typography>
									<Grid item display={'flex'} alignItems={'center'}>
										<>
											<SuccessIcon />

											<Typography variant={'caption'} ml={1.5} sx={{ textTransform: 'capitalize' }} mr={2}>
												{'Signed'}
											</Typography>
										</>

										<Divider orientation="vertical" flexItem />

										<Box ml={1}>
											{showDownloadButton ? (
												<IconButton
													onClick={handleDownloadApplicationPDF}
													disabled={isApplicationPDFDownloading}
													aria-label="Download tax form"
												>
													{isApplicationPDFDownloading ? (
														<CircularProgress size={24} sx={{ color: 'inherit' }} />
													) : (
														<Download />
													)}
												</IconButton>
											) : (
												<IconButton disabled>
													<Verified />
												</IconButton>
											)}
										</Box>
									</Grid>
								</Stack>
							</Grid>
						</Grid>
					</Grid>
				</>
			) : null}

			{isAllowRecertifyW8Ben ? (
				<>
					<StyledDivider orientation="horizontal" flexItem />

					<Grid item xs={12} px={2}>
						<Grid container rowSpacing={2}>
							<Grid item xs={12}>
								<Stack alignItems={'center'} justifyContent={'space-between'} direction={'row'}>
									<Typography variant={'caption'}>
										<b>Recertify W-8 BEN</b>
									</Typography>
									<Grid item display={'flex'} alignItems={'center'}>
										<Button
											variant={'contained'}
											color={'secondary'}
											size={'small'}
											onClick={handleRecertifyW8Ben}
											disabled={isPendingRecertifyW8BenForm}
											sx={{ textTransform: 'initial', width: 150, height: 32 }}
										>
											{isPendingRecertifyW8BenForm ? (
												<CircularProgress size={15} color={'info'} variant={'indeterminate'} />
											) : (
												<Typography variant={'caption'} ml={1}>
													<b>Recertify W-8 BEN</b>
												</Typography>
											)}
										</Button>
									</Grid>
								</Stack>
							</Grid>
						</Grid>
					</Grid>
				</>
			) : null}
		</StyledInfoSection>
	);
}

export default DetailsSection;
